import './Player.css';

import React, {useContext, useEffect, useRef} from 'react';
import { PlayerContext } from './PlayerContext';
import {browserLocales, getTextWidth, getCurrencySymbol, openFullscreen, translatePlaceType} from './Utils';
import LocaleCurrency from 'locale-currency';
import { useAnimate } from "framer-motion";
import { useTranslation } from 'react-i18next';
import { analyticsManager } from '../index';
import HoursButton from './side_menu/buttons/HoursButton';
import { applyType, bookType, contactType, orderType } from './side_menu/components/PrimaryButton';
import { getRestaurantInfosDisplay, hasChainRestaurants, storyHasOrderingPlatforms } from './chainRestaurantUtils';
import { getStoredPixelConsent } from './PixelConsentOverlay';
import MyLocationIcon from '@mui/icons-material/MyLocation';

function splitRestaurantNameIntoTwoLines(name, firstLineMaxWidth, secondLineMaxWidth, font) {
    if (!name) {
        return { line1: '', line2: '' };
    }
    if (getTextWidth(name, font) <= firstLineMaxWidth) {
        return { line1: name, line2: '' };
    }

    let line1End = 0;
    let lastSpace = -1;
    for (let i = 1; i <= name.length; i++) {
        if (name[i - 1] === ' ') {
            lastSpace = i;
        }
        if (getTextWidth(name.slice(0, i), font) > firstLineMaxWidth) {
            if (lastSpace > 0) {
                line1End = lastSpace;
            } else {
                line1End = Math.max(1, i - 1);
            }
            break;
        }
        line1End = i;
    }

    const line1 = name.slice(0, line1End).trimEnd();
    let line2 = name.slice(line1End).trimStart();

    if (line2 && getTextWidth(line2, font) > secondLineMaxWidth) {
        let truncated = line2;
        while (truncated.length > 1 && getTextWidth(truncated + '…', font) > secondLineMaxWidth) {
            truncated = truncated.slice(0, -1);
        }
        line2 = truncated + '…';
    }

    return { line1, line2 };
}

function RestaurantInfos(props){

    const { t } = useTranslation();

    const tag1Text = new URLSearchParams(window.location.search).get("tag1text");
    let tag1Color = new URLSearchParams(window.location.search).get("tag1color");
    tag1Color = tag1Color ? "#" + tag1Color : "#FFFFFF";
    let tag1TextColor = new URLSearchParams(window.location.search).get("tag1textcolor");
    tag1TextColor = tag1TextColor ? "#" + tag1TextColor : props.story.primaryColor;
    const tag2Text = new URLSearchParams(window.location.search).get("tag2text");
    let tag2Color = new URLSearchParams(window.location.search).get("tag2color");
    tag2Color = tag2Color ? "#" + tag2Color : "#FFFFFF";
    let tag2TextColor = new URLSearchParams(window.location.search).get("tag2textcolor");
    tag2TextColor = tag2TextColor ? "#" + tag2TextColor : props.story.primaryColor;
    const tag3Text = new URLSearchParams(window.location.search).get("tag3text");
    let tag3Color = new URLSearchParams(window.location.search).get("tag3color");
    tag3Color = tag3Color ? "#" + tag3Color : "#FFFFFF";
    let tag3TextColor = new URLSearchParams(window.location.search).get("tag3textcolor");
    tag3TextColor = tag3TextColor ? "#" + tag3TextColor : props.story.primaryColor;
    const storyDimensions = useContext(PlayerContext).get("storyDimensions");
    const highlight = useContext(PlayerContext).get("highlight");
    const setHighlight = useContext(PlayerContext).get("setHighlight");
    const sizeMultiplier = useContext(PlayerContext).get("sizeMultiplier");
    const setActiveFeature = useContext(PlayerContext).get("setActiveFeature");
    const openChainRestaurantPicker = useContext(PlayerContext).get("openChainRestaurantPicker");
    const pauseVideo = useContext(PlayerContext).get("pauseVideo");
    const playMode = useContext(PlayerContext).get("playMode");
    const appAnalyticsManager = useContext(PlayerContext).get("appAnalyticsManager");
    const primaryColor = useContext(PlayerContext).get("primaryColor");
    const secondaryColor = useContext(PlayerContext).get("secondaryColor");
    const playAudio = useContext(PlayerContext).get("playAudio");
    const playerOnDesktop = useContext(PlayerContext).get("playerOnDesktop");
    const fromMaker = useContext(PlayerContext).get("fromMaker");
    const fromDemo = useContext(PlayerContext).get("fromDemo");
    const setPlayAudio = useContext(PlayerContext).get("setPlayAudio");
    const audioAlreadyDisabled = useContext(PlayerContext).get("audioAlreadyDisabled");
    const setShareButtonAlreadyHighlighted = useContext(PlayerContext).get("setShareButtonAlreadyHighlighted");
    const shareButtonAlreadyHighlighted = useContext(PlayerContext).get("shareButtonAlreadyHighlighted");
    const insideStickerClicked = useContext(PlayerContext).get("insideStickerClicked");
    const menuStickerClicked = useContext(PlayerContext).get("menuStickerClicked");
    const idPrefix = fromDemo ? (fromDemo +"-") : "";
    const [scope, animate] = useAnimate();

    // 8 left margin of restaurant info - 4 space between restaurant infos and button - 8 right margin of the button
    const allowedWidthForInfos = storyDimensions.containerWidth - 8 * sizeMultiplier - 16 * sizeMultiplier - 32 * sizeMultiplier - 16 * sizeMultiplier;

    let btnType = null; 
    let btnTypeText = null;
    if (!props.story){
        btnType = null;
        btnTypeText = null;
    }
    else if (props.storyId === "cJU0Fnom8MTjmokacI3I" || props.storyId === "WX2KsZRK4SNKT9hnNIp0"){
        btnType = applyType;
        btnTypeText = "ApplyButton";
    }
    else if ((props.story.posBookingLink == null || props.story.posBookingLink === "none") && (props.story.posPhone && props.story.posPhone.trim() !== "" && props.story.posBookingByPhone !== false || props.story.posChatLink) && !storyHasOrderingPlatforms(props.story) /*&& !props.story.posBookingByPhone*/ || props.story.posId === "ChIJC4g4KBNx5kcRH_z3vKUpnZw"){
        btnType = contactType;
        btnTypeText = "ContactButton";
    }
    else if ((props.story.posBookingLink == null || props.story.posBookingLink === "none") && storyHasOrderingPlatforms(props.story)){
        btnType = orderType;
        btnTypeText = "OrderButton";
    }
    else if (props.story.posBookingLink != null && props.story.posBookingLink !== "none" || props.story.posBookingByPhone !== false && props.story.posPhone != null && props.story.posPhone.trim() !== "") {
        btnType = bookType;
        btnTypeText = "BookButton";
    }

    let ctaControls = useRef(null);
    function playAnimation(){
        const shareBtn = document.getElementById("btn-cta");          
        if (shareBtn != null){              
            const animationSequence = [                      
                [shareBtn, { backgroundColor: "#FFFFFF"}, {ease: "easeIn", duration: 0.6}],
                [shareBtn, { backgroundColor: "#FFFFFF"}, {duration: 0.2}],
                [shareBtn, { backgroundColor: props.story.primaryColor}, {ease: "easeOut", duration: 1.2}],
            ];            
            
            ctaControls.current = animate(animationSequence);        
        }
    }
    
    useEffect(() => {             
        if (! fromMaker && ! fromDemo && props.story && props.show && !shareButtonAlreadyHighlighted && (props.story.tourMediasRef || props.story.mediasRef || props.story.menuId) && (insideStickerClicked || !props.story.tourMediasRef) && (menuStickerClicked || !props.story.mediasRef && !props.story.menuId)){                        
            setTimeout(()=>playAnimation(), 1000);             
            setShareButtonAlreadyHighlighted(true);                 
        }        

    }, [props.story, props.show, shareButtonAlreadyHighlighted, insideStickerClicked, menuStickerClicked]);

    useEffect(() => {
        if (highlight != null){

            let elementId = null;
            let elementId2 = null;
            
            if (highlight.includes("restaurantInfosName")){
                elementId = "restaurant-infos-name";
            }
            else if (highlight.includes("restaurantInfos")){
                elementId = "restaurant-infos-container";
            }
            else if (highlight.includes("address")){
                elementId = "restaurant-infos-address";
            }   
            if (highlight.includes("book") && btnType === bookType){
                elementId2 = "btn-cta";                            
            }            
            else if (highlight.includes("contact") && btnType === contactType){
                elementId2 = "btn-cta";                
            }
            else if (highlight.includes("order") && btnType === orderType){
                elementId2 = "btn-cta";                
            }

            const element = document.getElementById(elementId);
            if (elementId != null && element != null){                   
                const animation = async () => {
                    await animate(element, { background: "#FFFFFFFF"}, {duration: 0.6});
                    if (element != null)
                        animate(element, { background: "#FFFFFF00"}, {duration: 0.6});
                }
                
                animation();
            }
            const element2 = document.getElementById(elementId2);
            if (elementId2 != null && element2 != null){   
                const element2BGColor = element2.style.backgroundColor;                
                const animation = async () => {
                    await animate(element2, { background: "#FFFFFFFF"}, {duration: 0.6});
                    if (element2 != null)
                        animate(element2, { background: element2BGColor}, {duration: 0.6});
                }
                
                animation();
            }
        }
      }, [highlight, animate]);

    const restaurantDisplay = getRestaurantInfosDisplay(props.story);

    if (restaurantDisplay.posName != null && props.show){  
                                
        let businessTypePriceLevel = "";
        if (props.story.posType != null) {    
            const language = browserLocales(true)[0] === "fr" ? "fr" : "en";           
            businessTypePriceLevel += translatePlaceType(props.story.posType, language);        
            if (props.story.priceLevel != null){               
                businessTypePriceLevel += " · ";
            }
        }

        if (props.story.priceLevel != null){
            let locale = browserLocales()[0];
            //log("browserLocales : " + browserLocales()[0]);
            let currencySymbol = getCurrencySymbol(locale, LocaleCurrency.getCurrency(locale));
                        
            if (props.story.priceLevel > 0){
                businessTypePriceLevel +=  currencySymbol.repeat(props.story.priceLevel);    
            }
            else if (props.story.priceLevel === 0){
                businessTypePriceLevel +=  t('Free');    
            }           
        }        
        
        const displayedName = restaurantDisplay.posName;
        const displayedAddress = restaurantDisplay.address;
        const showChainChangeButton = hasChainRestaurants(props.story);
        const nameLineHeight = 16 * 1.2 * sizeMultiplier;
        // Tailles entières : évite le décentrage 1px lié au sous-pixel (flex + sizeMultiplier).
        const chainChangeButtonSize = Math.round(nameLineHeight);
        let chainChangeIconSize = Math.round(14 * sizeMultiplier);
        if ((chainChangeButtonSize - chainChangeIconSize) % 2 !== 0) {
            chainChangeIconSize = Math.max(1, chainChangeIconSize - 1);
        }
        const chainChangeButtonGap = 12 * sizeMultiplier;
        const chainChangeButtonReservedWidth = showChainChangeButton ? chainChangeButtonSize + chainChangeButtonGap : 0;
        const allowedWidthForText = allowedWidthForInfos - 16 * sizeMultiplier;
        const allowedWidthForName = allowedWidthForText - chainChangeButtonReservedWidth;
        const nameFont = "bold " + 16 * sizeMultiplier + "px Roboto";
        // Avec le bouton chaîne, la décision 1/2 lignes doit utiliser la largeur restante
        const nameWidthForWrapDecision = showChainChangeButton ? allowedWidthForName : allowedWidthForText;
        const nameOn2lines = getTextWidth(displayedName, nameFont) > nameWidthForWrapDecision;
        const nameLines = (showChainChangeButton && nameOn2lines)
            ? splitRestaurantNameIntoTwoLines(displayedName, allowedWidthForText, allowedWidthForName, nameFont)
            : null;
        const addressOn2lines = displayedAddress && getTextWidth(displayedAddress, "italic " + 12 * sizeMultiplier + "px Roboto") > allowedWidthForText;    
        
        const infosHeight = (16 * 1.2 * (nameOn2lines ? 2 : 1) + 2 + 12 * 1.2 + (displayedAddress ? 2 + 12 * 1.2 * (!nameOn2lines && addressOn2lines ? 2 : 1) : 0) + 8 + 34 + (btnType != null ? (8 + 48) : 0) + 16 + (tag1Text ? 8 + 14 * 1.2 + 2 * 4 : 0)) * sizeMultiplier;
        const topMargin = storyDimensions.containerHeight - infosHeight;
        
        const addressStyle = {};
        if (!nameOn2lines){
            addressStyle.display = 'flex';
            addressStyle.flexDirection = 'row';
            addressStyle.lineClamp = 2;
            addressStyle.display = '-webkit-box';            
            addressStyle.WebkitLineClamp = 2;
            addressStyle.WebkitBoxOrient = 'vertical';
        }  
        else {
            addressStyle.whiteSpace = 'nowrap';
        }

        const openLocation = () => { 
            if (!fromMaker /*&& !fromDemo*/ && !playerOnDesktop && !audioAlreadyDisabled && !playAudio){            
                const ambiantPlayer = document.getElementById(idPrefix + "audio-player");
                if (ambiantPlayer && ambiantPlayer.paused){
                    try {
                        ambiantPlayer.play();                
                    } catch(error){}
                }
                setPlayAudio(true);
            }
            if (hasChainRestaurants(props.story) && !props.story.posId) {
                setActiveFeature("location");
                return;
            }
            pauseVideo();
            openFullscreen();
            setActiveFeature("location");
             
            analyticsManager.logCheckItineraryTag(props.storyId, playMode);
            if (appAnalyticsManager)
                appAnalyticsManager.clickedLocation();
            if (getStoredPixelConsent() === 'accepted') {
                if (window.ttq){
                    window.ttq.track('ViewContent', {                
                        "contents": [
                            {              
                                "content_id": "0",           
                                "content_name": "map",
                            }
                        ],
                    });
                }
                if (window.fbq){                    
                    window.fbq('track', 'ViewContent', {"content_name": "map"});
                }
            }
        };

        const openBook = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!fromMaker /*&& !fromDemo*/ && !playerOnDesktop && !audioAlreadyDisabled && !playAudio){            
                const ambiantPlayer = document.getElementById(idPrefix + "audio-player");
                if (ambiantPlayer && ambiantPlayer.paused){
                    try {
                        ambiantPlayer.play();                
                    } catch(error){}
                }
                setPlayAudio(true);
            }
            pauseVideo();
            openFullscreen();
            setActiveFeature("book");
            analyticsManager.logOpenPopupTag(props.storyId, "book", true, playMode);
            if (appAnalyticsManager)
                appAnalyticsManager.clickedBook();

            if (getStoredPixelConsent() === 'accepted') {
                if (btnType === bookType){
                    if (window.ttq){
                        window.ttq.track('InitiateBooking', {                
                            "contents": [
                                {              
                                    "content_id": "0",           
                                    "content_name": "main " + btnType + " button",
                                }
                            ],
                        });
                    }
                    if (window.fbq){                    
                        window.fbq('trackCustom', 'InitiateBooking', {"content_name": "main " + btnType + " button"});
                    }
                }
                else if (btnType === contactType){
                    if (window.ttq){
                        window.ttq.track('InitiateContact', {                
                            "contents": [
                                {              
                                    "content_id": "0",           
                                    "content_name": "main " + btnType + " button",
                                }
                            ],
                        });
                    }
                    if (window.fbq){                    
                        window.fbq('trackCustom', 'InitiateContact', {"content_name": "main " + btnType + " button"});
                    }
                }
                else if (btnType === orderType){
                    if (window.ttq){
                        window.ttq.track('InitiateOrder', {                
                            "contents": [
                                {              
                                    "content_id": "0",           
                                    "content_name": "main " + btnType + " button",
                                }
                            ],
                        });
                    }
                    if (window.fbq){                    
                        window.fbq('trackCustom', 'InitiateOrder', {"content_name": "main " + btnType + " button"});
                    }
                }
                else if (btnType === applyType){
                    if (window.ttq){
                        window.ttq.track('InitiateApply', {                
                            "contents": [
                                {              
                                    "content_id": "0",           
                                    "content_name": "main " + btnType + " button",
                                }
                            ],
                        });
                    }
                    if (window.fbq){                    
                        window.fbq('trackCustom', 'InitiateApply', {"content_name": "main " + btnType + " button"});
                    }
                }
            }
        };

        const openChangeChainRestaurant = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!fromMaker && !playerOnDesktop && !audioAlreadyDisabled && !playAudio) {
                const ambiantPlayer = document.getElementById(idPrefix + "audio-player");
                if (ambiantPlayer && ambiantPlayer.paused) {
                    try {
                        ambiantPlayer.play();
                    } catch (error) {}
                }
                setPlayAudio(true);
            }
            if (openChainRestaurantPicker) {
                openChainRestaurantPicker();
            }
        };

        const chainChangeButtonStyle = {
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',            
            width: chainChangeButtonSize,
            height: chainChangeButtonSize,
            padding: 0,
            margin: 0,
            border: 'none',
            borderRadius: Math.round(4 * sizeMultiplier),
            backgroundColor: '#FFFFFF',
            cursor: 'pointer',
            lineHeight: 0,
            fontSize: 0,
            boxSizing: 'border-box',
            overflow: 'hidden',
            WebkitAppearance: 'none',
            appearance: 'none',
        };

        function renderChainChangeButton(extraStyle = {}) {
            return (
                <button
                    type="button"
                    onClick={openChangeChainRestaurant}
                    title={t('ChainChangeRestaurant')}
                    aria-label={t('ChainChangeRestaurant')}
                    style={{ ...chainChangeButtonStyle, ...extraStyle }}
                >
                    <span
                        aria-hidden
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: chainChangeIconSize,
                            height: chainChangeIconSize,
                            lineHeight: 0,
                            flexShrink: 0,
                        }}
                    >
                        <MyLocationIcon
                            htmlColor={primaryColor}
                            style={{
                                display: 'block',
                                fontSize: chainChangeIconSize,
                                width: chainChangeIconSize,
                                height: chainChangeIconSize,
                                margin: 0,
                                padding: 0,
                            }}
                        />
                    </span>
                </button>
            );
        }

        return (
            <>
                <div>
                    <div ref={scope} id="restaurant-infos-container" name="restaurant-infos-container" className="RestaurantInfosContainer" style={{marginTop: topMargin, left: 0 * sizeMultiplier, width: allowedWidthForInfos,}} >    
                        {tag1Text &&
                            <div style={{display: 'flex', flexDirection: 'row', fontSize: 14 * sizeMultiplier, fontWeight: 'bold', marginBottom: 8 * sizeMultiplier, marginLeft: 16 * sizeMultiplier}}>
                                <div style={{borderRadius: 8, border: '0px', paddingLeft: 8 * sizeMultiplier, paddingRight: 8 * sizeMultiplier, paddingTop: 4 * sizeMultiplier, paddingBottom: 4 * sizeMultiplier, color: tag1TextColor, backgroundColor: tag1Color}}>
                                    {tag1Text}
                                </div>
                                {tag2Text &&
                                    <div style={{marginLeft: 8 * sizeMultiplier, borderRadius: 8, border: '0px', paddingLeft: 8  * sizeMultiplier, paddingRight: 8 * sizeMultiplier, paddingTop: 4 * sizeMultiplier, paddingBottom: 4 * sizeMultiplier, color: tag2TextColor, backgroundColor: tag2Color}}>
                                    {tag2Text}
                                    </div>  
                                }
                                {tag3Text &&
                                    <div style={{marginLeft: 8 * sizeMultiplier, borderRadius: 8, border: '0px', paddingLeft: 8  * sizeMultiplier, paddingRight: 8 * sizeMultiplier, paddingTop: 4 * sizeMultiplier, paddingBottom: 4 * sizeMultiplier, color: tag3TextColor, backgroundColor: tag3Color}}>
                                    {tag3Text}
                                    </div>  
                                }
                            </div>
                        }
                        <div className="RestaurantDescriptionContainer" style={{fontSize: 12 * sizeMultiplier}}>                            
                            <div style={{position: 'relative', left: 16 * sizeMultiplier}}>
                                {showChainChangeButton && nameOn2lines ? (
                                    <div
                                        id="restaurant-infos-name"
                                        name="restaurant-infos-name"
                                        style={{ width: allowedWidthForText }}
                                    >
                                        <div
                                            className="RestaurantName"
                                            style={{
                                                fontSize: 16 * sizeMultiplier,
                                                fontStyle: 'normal',
                                                lineHeight: '1.2',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {nameLines.line1}
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'flex-start',
                                                width: '100%',
                                                gap: chainChangeButtonGap,
                                            }}
                                        >
                                            <div
                                                className="RestaurantName"
                                                style={{
                                                    minWidth: 0,
                                                    flex: 1,
                                                    fontSize: 16 * sizeMultiplier,
                                                    fontStyle: 'normal',
                                                    lineHeight: '1.2',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {nameLines.line2}
                                            </div>
                                            {renderChainChangeButton(
                                                { marginLeft: 'auto' },
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: 'inline-flex', flexDirection: 'row', alignItems: 'flex-start', maxWidth: allowedWidthForText }}>
                                        <div
                                            id="restaurant-infos-name"
                                            name="restaurant-infos-name"
                                            className="RestaurantName"
                                            style={{
                                                minWidth: 0,
                                                maxWidth: showChainChangeButton ? allowedWidthForName : allowedWidthForText,
                                                fontSize: 16 * sizeMultiplier,
                                                fontStyle: 'normal',
                                                lineHeight: '1.2',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {displayedName}
                                        </div>
                                        {showChainChangeButton && renderChainChangeButton(
                                            { marginLeft: chainChangeButtonGap },
                                        )}
                                    </div>
                                )}
                                {businessTypePriceLevel && <div  style={{marginTop: 2*sizeMultiplier, width: allowedWidthForText, lineHeight: '1.2'}} className='not-selectable'>{businessTypePriceLevel}</div>}
                                {displayedAddress && <div id="restaurant-infos-address" name="restaurant-infos-address" className='RestaurantBusinessAddress' style={{marginTop: 2*sizeMultiplier, width: allowedWidthForText, ...addressStyle}}>{displayedAddress}</div>}
                            </div>
                        </div>
                        <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-evenly', gap: 8 * sizeMultiplier, marginLeft: 16 * sizeMultiplier, marginTop: 8 * sizeMultiplier, width: allowedWidthForText}}>                            
                            <HoursButton show={true} storyId={props.storyId} story={props.story} placeDetails={props.placeDetails}/>                                                      
                            <button 
                                onClick={openLocation}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 6 * sizeMultiplier,
                                    padding: `${8 * sizeMultiplier}px ${12 * sizeMultiplier}px`,
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    borderRadius: 8 * sizeMultiplier,
                                    color: 'white',
                                    fontSize: 14 * sizeMultiplier,
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    fontFamily: 'Roboto, sans-serif'
                                }}
                            >
                                <img src="/images/ic_location_v2.png" alt="" aria-hidden="true" style={{width: 18 * sizeMultiplier, height: 18 * sizeMultiplier}} />
                                <span style={{whiteSpace: 'nowrap'}}>{t("Location")}</span>
                            </button>
                        </div>
                        {btnType != null && <div style={{marginTop: 8 * sizeMultiplier}}></div>}
                        {btnType != null && <button 
                            id="btn-cta"
                            name="btn-cta"
                            onClick={openBook}
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-between',                                                        
                                marginLeft: 16 * sizeMultiplier,
                                marginRight: 8 * sizeMultiplier,
                                height: 48 * sizeMultiplier,
                                padding: 16 * sizeMultiplier,
                                backgroundColor: primaryColor,
                                border: 'none',
                                borderRadius: 8 * sizeMultiplier,
                                color: secondaryColor,
                                fontSize: 14 * sizeMultiplier,
                                fontWeight: 600,
                                cursor: 'pointer',
                                fontFamily: 'Roboto, sans-serif',
                                width: allowedWidthForInfos - 16 * sizeMultiplier
                            }}
                        >
                            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 * sizeMultiplier}}>                            
                                <span style={{textTransform: 'capitalize'}}>{t(btnTypeText)}</span>
                                {/*props.story.orderingPlatforms != null && btnType === bookType && <div style={{
                                    marginLeft: 8 * sizeMultiplier,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 20 * sizeMultiplier,
                                    height: 20 * sizeMultiplier,
                                    borderRadius: '50%',
                                    backgroundColor: 'transparent',
                                    border: '1px solid ' + props.story.secondaryColor
                                }}>
                                    <div style={{
                                        width: 14 * sizeMultiplier,
                                        height: 14 * sizeMultiplier,
                                        backgroundColor: props.story.secondaryColor,
                                        maskImage: 'url(/images/ic_moped.png)',
                                        maskSize: 'contain',
                                        maskRepeat: 'no-repeat',
                                        maskPosition: 'center',
                                        WebkitMaskImage: 'url(/images/ic_moped.png)',
                                        WebkitMaskSize: 'contain',
                                        WebkitMaskRepeat: 'no-repeat',
                                        WebkitMaskPosition: 'center'
                                    }} />
                                </div>*/}
                            </div>
                            <div style={{
                                width: 18 * sizeMultiplier,
                                height: 18 * sizeMultiplier,
                                backgroundColor: props.story.secondaryColor,
                                maskImage: 'url(/images/ic_arrow_forward.png)',
                                maskSize: 'contain',
                                maskRepeat: 'no-repeat',
                                maskPosition: 'center',
                                WebkitMaskImage: 'url(/images/ic_arrow_forward.png)',
                                WebkitMaskSize: 'contain',
                                WebkitMaskRepeat: 'no-repeat',
                                WebkitMaskPosition: 'center'
                            }} />
                        </button>}
                    </div>
                </div>
            </>
        );

    }
    else {
        return null;
    }
    
}

export default RestaurantInfos;